import { createRoute } from "@tanstack/react-router";
import { ReportScreen } from "@/app/Reports/Reports/Screens/ReportScreen";
import { ReportSelectionScreen } from "@/app/Reports/Reports/Screens/ReportSelectionScreen";
import { ReportRelocationScreen } from "@/app/Reports/Reports/Screens/ReportRelocationScreen";
import { ReportSalesScreen } from "@/app/Reports/Reports/Screens/ReportSalesScreen";
import { ReportBookingsScreen } from "@/app/Reports/Reports/Screens/ReportBookingsScreen";
import { ReportInvoicesScreen } from "@/app/Reports/Reports/Screens/ReportInvoicesScreen";
import { ReportWorkOrdersScreen } from "@/app/Reports/Reports/Screens/ReportWorkOrdersScreen";
import { ReportAssignmentsScreen } from "@/app/Reports/Reports/Screens/ReportAssignmentsScreen";
import { ReportReceiptsScreen } from "@/app/Reports/Reports/Screens/ReportReceiptsScreen";
import { ReceivablesReconciliationReportScreen } from "@/app/Reports/Reports/Screens/ReceivablesReconciliationReportScreen";
import { SupplierReportScreen } from "@/app/Reports/SupplierReport/Screens/SupplierReportScreen";
import { ProDriverSupplierReportScreen } from "@/app/Reports/ProDriverSupplierReport/Screens/ProDriverSupplierReportScreen";
import { ReportRelocationFlowScreen } from "@/app/Reports/RelocationFlow/Screens/ReportRelocationFlowScreen";
import { z } from "zod";
import dayjs from "dayjs";
import { appLayoutRoute } from "@/routes/layout-routes";
import { AdminReportingScreen } from "@/app/Reports/Reports/Screens/AdminReportingScreen";

export const reportRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/reports",
  component: ReportScreen,
  head: () => ({
    meta: [
      {
        title: "Reports - Imoova Admin",
      },
    ],
  }),
});
export const reportSelectionRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/",
  component: ReportSelectionScreen,
  head: () => ({
    meta: [
      {
        title: "Reports - Imoova Admin",
      },
      {
        name: "description",
        content: "View and generate reports",
      },
    ],
  }),
});

export const adminReportRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/admin",
  component: AdminReportingScreen,
  head: () => ({
    meta: [
      {
        title: "Admin Reporting - Imoova Admin",
      },
    ],
  }),
});

const reportBookingSearchParams = z.object({
  supplierIds: z.array(z.string()).optional(),
  startDate: z
    .string()
    .catch(dayjs().subtract(30, "days").format("YYYY-MM-DD")),
  endDate: z.string().catch(dayjs().format("YYYY-MM-DD")),
  dateBasis: z
    .enum(["DELIVER", "DEPART", "BOOKED", "CONFIRMED", "POSTED"])
    .catch("DELIVER"),
});
export const reportBookingsRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/bookings",
  validateSearch: reportBookingSearchParams,
  component: ReportBookingsScreen,
});

const reportInvoiceSearchParams = z.object({
  startDate: z
    .string()
    .catch(dayjs().subtract(30, "days").format("YYYY-MM-DD")),
  endDate: z.string().catch(dayjs().format("YYYY-MM-DD")),
});
export const reportInvoicesRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/invoices",
  validateSearch: reportInvoiceSearchParams,
  component: ReportInvoicesScreen,
});

const reportWorkOrderSearchParams = z.object({
  quarter: z.number().catch(getAustralianFYQuarter().quarter),
  year: z.number().catch(getAustralianFYQuarter().year),
});
export const reportWorkOrdersRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/work-orders",
  validateSearch: reportWorkOrderSearchParams,
  component: ReportWorkOrdersScreen,
});

const reportAssignmentsSearchParams = z.object({
  quarter: z
    .number()
    .int()
    .min(1)
    .max(4)
    .catch(getAustralianFYQuarter().quarter),
  year: z.number().catch(getAustralianFYQuarter().year),
});
export const reportAssignmentsRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/assignments",
  validateSearch: reportAssignmentsSearchParams,
  component: ReportAssignmentsScreen,
  head: () => ({
    meta: [
      {
        title: "Work Order Assignments Report - Imoova Admin",
      },
    ],
  }),
});

const proDriverSupplierReportSearchParams = z.object({
  quarter: z.number().catch(getAustralianFYQuarter().quarter),
  year: z.number().catch(getAustralianFYQuarter().year),
});
export const reportProDriverSuppliersRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/pro-driver-suppliers",
  validateSearch: proDriverSupplierReportSearchParams,
  component: ProDriverSupplierReportScreen,
  head: () => ({
    meta: [
      {
        title: "Pro Driver Supplier Report - Imoova Admin",
      },
    ],
  }),
});

const reportReceiptsSearchParams = z.object({
  quarter: z.number().catch(getAustralianFYQuarter().quarter),
  year: z.number().catch(getAustralianFYQuarter().year),
});
export const reportReceiptsRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/receipts",
  validateSearch: reportReceiptsSearchParams,
  component: ReportReceiptsScreen,
});

const reportRelocationSearchParams = z.object({
  startDate: z
    .string()
    .catch(dayjs().subtract(30, "days").format("YYYY-MM-DD")),
  endDate: z.string().catch(dayjs().format("YYYY-MM-DD")),
});
export const reportRelocationsRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/relocations",
  validateSearch: reportRelocationSearchParams,
  component: ReportRelocationScreen,
  head: () => ({
    meta: [
      {
        title: "Relocation Reports - Imoova Admin",
      },
    ],
  }),
});

// Australian Financial Year: Q1=Jul-Sep, Q2=Oct-Dec, Q3=Jan-Mar, Q4=Apr-Jun
// FY year is the year the FY ends (e.g., FY2025 = Jul 2024 - Jun 2025)
function getAustralianFYQuarter() {
  const month = new Date().getMonth() + 1; // 1-12
  const calendarYear = new Date().getFullYear();

  if (month >= 7 && month <= 9) return { quarter: 1, year: calendarYear + 1 };
  if (month >= 10 && month <= 12) return { quarter: 2, year: calendarYear + 1 };
  if (month >= 1 && month <= 3) return { quarter: 3, year: calendarYear };
  return { quarter: 4, year: calendarYear }; // Apr-Jun
}

const salesCommissionSearchParams = z.object({
  quarter: z.number().catch(getAustralianFYQuarter().quarter),
  year: z.number().catch(getAustralianFYQuarter().year),
  userId: z.string().optional(),
});

export const reportSalesRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/sales",
  validateSearch: salesCommissionSearchParams,
  component: ReportSalesScreen,
  head: () => ({
    meta: [
      {
        title: "Commission Report - Imoova Admin",
      },
    ],
  }),
});

const supplierReportSearchParams = z.object({
  startDate: z
    .string()
    .catch(dayjs().subtract(30, "days").format("YYYY-MM-DD")),
  endDate: z.string().catch(dayjs().format("YYYY-MM-DD")),
});

export const reportSuppliersRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/suppliers",
  validateSearch: supplierReportSearchParams,
  component: SupplierReportScreen,
});

export const receivablesReconciliationReportRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/receivables-reconciliation",
  component: ReceivablesReconciliationReportScreen,
  head: () => ({
    meta: [
      {
        title: "Receivables Reconciliation Report - Imoova Admin",
      },
    ],
  }),
});

const relocationFlowSearchParams = z
  .object({
    year: z.number().catch(new Date().getFullYear()),
    month: z
      .number()
      .nullable()
      .catch(new Date().getMonth() + 1),
  })
  .transform(({ year, month }) => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const clampedYear = Math.min(year, currentYear);
    const clampedMonth =
      month === null
        ? null
        : Math.min(month, clampedYear >= currentYear ? currentMonth : 12);
    return { year: clampedYear, month: clampedMonth };
  });

export const reportRelocationFlowRoute = createRoute({
  getParentRoute: () => reportRoute,
  path: "/relocation-flow",
  validateSearch: relocationFlowSearchParams,
  component: ReportRelocationFlowScreen,
  head: () => ({
    meta: [
      {
        title: "Relocation Flow Report - Imoova Admin",
      },
    ],
  }),
});
